/**
 * Máy chủ giả của domain thương hiệu (ADR-0097, tenant-service): `/api/v1/tenants/{id}/custom-hosts`.
 *
 * Từ chối y như hợp đồng (cùng mã HTTP, cùng `pointer`) để nhánh lỗi chạy được khi chưa có backend:
 * - hostname đã thuộc một tenant, kể cả `cu.sao-mai.vn` vừa gỡ còn trong 30 ngày giữ chỗ: `409 TENANT_DOMAIN_CONFLICT`;
 * - domain của nền tảng (`*.foxai.com.vn`, `*.fox.ai.vn`): `400 INVALID_ARGUMENT` trỏ `#/hostname`;
 * - tenant đã có đủ 3 domain chưa gỡ: `400 INVALID_ARGUMENT` trỏ `#/hostname`;
 * - kích hoạt domain chưa đạt DNS: `400 INVALID_ARGUMENT` trỏ `#/status`.
 *
 * Tình huống mẫu: FoxAI (`…0001`) có một domain đang dùng và một domain chờ DNS (chưa có TXT; bấm "Kiểm tra lại
 * DNS" thì sang Đã xác minh — diễn cảnh khách vừa tạo bản ghi); Minh Long (`…0002`, tenant của tài khoản mẫu cổng tổ chức) có một
 * domain đang dùng, một domain gốc chờ DNS (bản ghi A, ghi chú ALIAS) và một domain quản trị nền tảng đã tạm ngưng
 * và khoá vì CNAME trỏ sai. Máy chủ giả không biết ai gọi nên không chặn Tenant Admin kích hoạt domain bị khoá; giao
 * diện ẩn mục đó.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây (`modules/tenant/constants.ts`).
 */
import type { ApiError } from '../lib/errors';
import { chuanHoaHostname, kiemHostname, laDomainGoc } from '../modules/tenant/nghiep-vu/dia-chi-rieng';
import type { CustomHost, CustomHostList } from '../modules/tenant/types';

const FOXAI = '0a7d3c5e-1f2b-4e6a-9c8d-000000000001';
const MINH_LONG = '0a7d3c5e-1f2b-4e6a-9c8d-000000000002';

const TOI_DA = 3;
const DICH_CNAME = 'nativev4.foxai.com.vn';
const IP_NEN_TANG = ['103.157.218.12'];
const DANG_GIU_CHO = new Set(['cu.sao-mai.vn']);

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const NGAY = 24 * 60 * 60 * 1000;
const luc = (soNgay: number) => new Date(Date.now() + soNgay * NGAY).toISOString();

let dem = 0;
const token = () => `nv-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;

function dong(tenantId: string, hostname: string, p: Partial<CustomHost>): CustomHost {
  dem += 1;
  return {
    id: `ch-${String(dem).padStart(3, '0')}`,
    tenant_id: tenantId,
    hostname,
    status: 'CHO_DNS',
    apex: laDomainGoc(hostname),
    verify_token: token(),
    txt_record_name: `_native-verify.${hostname}`,
    cname_target: DICH_CNAME,
    target_ips: IP_NEN_TANG,
    dns_checked_at: null,
    dns_error: null,
    cert_status: null,
    cert_expires_at: null,
    created_by: 'u-001',
    created_at: luc(-10),
    updated_at: luc(-10),
    platform_locked: false,
    ...p,
  };
}

let ds: CustomHost[] = [
  dong(FOXAI, 'portal.foxai.vn', {
    status: 'DANG_DUNG',
    dns_checked_at: luc(-0.2),
    cert_status: 'VALID',
    cert_expires_at: luc(62),
    created_at: luc(-40),
  }),
  dong(FOXAI, 'app.foxai-group.com', {
    dns_checked_at: luc(-0.05),
    dns_error: 'TXT_MISSING',
    created_at: luc(-1),
  }),
  dong(MINH_LONG, 'app.minhlong.com.vn', {
    status: 'DANG_DUNG',
    dns_checked_at: luc(-0.3),
    cert_status: 'VALID',
    cert_expires_at: luc(10),
    created_at: luc(-60),
  }),
  dong(MINH_LONG, 'minhlong.vn', {
    dns_checked_at: luc(-0.5),
    created_at: luc(-3),
  }),
  dong(MINH_LONG, 'cong-thong-tin-noi-bo-chi-nhanh-mien-trung.minhlong.com.vn', {
    status: 'TAM_NGUNG',
    dns_checked_at: luc(-1),
    dns_error: 'TARGET_MISMATCH',
    created_at: luc(-20),
    platform_locked: true,
  }),
];

function tim(tenantId: string, hostId: string): CustomHost {
  const h = ds.find((x) => x.id === hostId && x.tenant_id === tenantId);
  if (!h) throw loi('NOT_FOUND', 404, 'Không tìm thấy domain thương hiệu này. Tải lại danh sách.');
  return h;
}

function sua(h: CustomHost, p: Partial<CustomHost>): CustomHost {
  const moi = { ...h, ...p, updated_at: new Date().toISOString() };
  ds = ds.map((x) => (x.id === h.id ? moi : x));
  return moi;
}

const loiTrangThai = (detail: string) =>
  loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên có 1 lỗi.', [{ pointer: '#/status', detail }]);

export const mayChuDiaChiRieng = {
  danhSach(tenantId: string): CustomHostList {
    return {
      items: ds.filter((h) => h.tenant_id === tenantId).sort((a, b) => b.created_at.localeCompare(a.created_at)),
      max_hosts: TOI_DA,
    };
  },

  them(tenantId: string, raw: string): CustomHost {
    const hostname = chuanHoaHostname(raw);
    const truong = (detail: string) =>
      loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên có 1 lỗi.', [{ pointer: '#/hostname', detail }]);
    if (kiemHostname(hostname)) throw truong('Hostname không hợp lệ. Nhập dạng app.congtyabc.com.vn.');
    if (/(^|\.)(foxai\.com\.vn|fox\.ai\.vn)$/.test(hostname)) {
      throw truong('Đây là domain của nền tảng, không dùng làm domain thương hiệu được. Nhập domain của khách.');
    }
    if (ds.some((h) => h.hostname === hostname) || DANG_GIU_CHO.has(hostname)) {
      throw loi(
        'TENANT_DOMAIN_CONFLICT',
        409,
        `Hostname ${hostname} đang thuộc một tenant hoặc vừa được gỡ và còn trong 30 ngày giữ chỗ. Chọn hostname khác.`,
      );
    }
    if (ds.filter((h) => h.tenant_id === tenantId).length >= TOI_DA) {
      throw truong(`Tenant đã có đủ ${TOI_DA} domain thương hiệu. Gỡ bớt một domain rồi thêm lại.`);
    }
    const moi = dong(tenantId, hostname, { created_at: new Date().toISOString() });
    ds = [moi, ...ds];
    return moi;
  },

  kiem(tenantId: string, hostId: string): CustomHost {
    const h = tim(tenantId, hostId);
    const bayGio = new Date().toISOString();
    // Khách vừa tạo bản ghi: lần kiểm sau khi bấm thì đạt.
    if (h.status === 'CHO_DNS') return sua(h, { status: 'DA_XAC_MINH', dns_error: null, dns_checked_at: bayGio });
    return sua(h, { dns_checked_at: bayGio });
  },

  kichHoat(tenantId: string, hostId: string): CustomHost {
    const h = tim(tenantId, hostId);
    if (h.status === 'DANG_DUNG') return h;
    if (h.status !== 'DA_XAC_MINH' && h.status !== 'TAM_NGUNG') {
      throw loiTrangThai('Domain chưa đạt kiểm tra DNS nên chưa kích hoạt được. Kiểm tra lại DNS trước.');
    }
    return sua(h, { status: 'DANG_DUNG', platform_locked: false });
  },

  tamNgung(tenantId: string, hostId: string): CustomHost {
    const h = tim(tenantId, hostId);
    if (h.status === 'TAM_NGUNG') return h;
    if (h.status !== 'DANG_DUNG') throw loiTrangThai('Chỉ tạm ngưng được domain đang dùng.');
    return sua(h, { status: 'TAM_NGUNG' });
  },

  go(tenantId: string, hostId: string): void {
    const h = tim(tenantId, hostId);
    ds = ds.filter((x) => x.id !== h.id);
    DANG_GIU_CHO.add(h.hostname);
  },
};
